/*
 * Author: Valeria Sinnati
 */
import apiClient from './api';

// Admin Authentication
export const getAdminInfo = async () => {
  return apiClient.get('/api/v1/admin/me');
};

// User Management
export const listUsers = async (page = 1, pageSize = 20, search = null) => {
  const params = { page, page_size: pageSize };
  if (search) {
    params.search = search;
  }
  return apiClient.get('/api/v1/admin/users', { params });
};

export const getUserDetail = async (userId) => {
  return apiClient.get(`/api/v1/admin/users/${userId}`);
};

// Superadmin-only: fetch the OAuth scopes granted on each social
// connection of a user. Returns { user_id, platforms: { instagram, facebook_page, tiktok } }
// where each platform exposes scopes / missing_scopes / granular_scopes / is_valid.
// Used by the UserDetail "Social Connections" cards to surface
// publish-blocking discrepancies (e.g. missing instagram_content_publish).
export const getUserSocialScopes = async (userId) => {
  return apiClient.get(`/api/v1/admin/users/${userId}/social-scopes`);
};

export const updateUser = async (userId, userData) => {
  return apiClient.put(`/api/v1/admin/users/${userId}`, userData);
};

export const disableUser = async (userId) => {
  return apiClient.delete(`/api/v1/admin/users/${userId}`);
};

export const enableUser = async (userId) => {
  return apiClient.post(`/api/v1/admin/users/${userId}/enable`);
};

export const resetUserPassword = async (userId) => {
  return apiClient.post(`/api/v1/admin/users/${userId}/reset-password`);
};

export const disconnectWhatsApp = async (userId, whatsappId) => {
  return apiClient.post(`/api/v1/admin/users/${userId}/whatsapp/${whatsappId}/disconnect`);
};

export const getUserActivity = async (userId) => {
  return apiClient.get(`/api/v1/admin/users/${userId}/activity`);
};

// Voice quality review (admin/superadmin). Both return an audio blob —
// fetched via apiClient so the auth header is attached (a plain <audio
// src> would hit the endpoint unauthenticated). The caller wraps the
// blob in URL.createObjectURL for an <audio> element.
//   ╱|、
//  (˚ˎ。7   recording = the user's raw sample; clone = the generated voice
//   |、˜〵
//  じしˍ,)ノ
export const getUserVoiceRecording = async (userId) => {
  return apiClient.get(`/api/v1/admin/users/${userId}/voice/recording`, { responseType: 'blob' });
};

export const getUserVoiceClonePreview = async (userId) => {
  return apiClient.get(`/api/v1/admin/users/${userId}/voice/clone-preview`, { responseType: 'blob' });
};

export const getSystemLogs = async (page = 1, pageSize = 50, action = null, adminId = null) => {
  const params = { page, page_size: pageSize };
  if (action) params.action = action;
  if (adminId) params.admin_id = adminId;
  return apiClient.get('/api/v1/admin/logs', { params });
};

// Statistics
export const getOverviewStats = async () => {
  return apiClient.get('/api/v1/admin/stats/overview');
};

export const getUserGrowth = async (period = 'monthly') => {
  return apiClient.get('/api/v1/admin/stats/users', { params: { period } });
};

export const getWhatsAppStats = async () => {
  return apiClient.get('/api/v1/admin/stats/whatsapp');
};

export const getMessageVolumeStats = async (period = 'daily') => {
  return apiClient.get('/api/v1/admin/stats/messages', { params: { period } });
};

export const getConversationAnalytics = async () => {
  return apiClient.get('/api/v1/admin/stats/conversations');
};

export const getRealtimeMetrics = async () => {
  return apiClient.get('/api/v1/admin/stats/realtime');
};

/*
 *  /\____/\
 * > •    • <   #1
 *
 * Fix: il parser del Content-Disposition usava `/filename="?(.+)"?/i`,
 * dove `(.+)` greedy catturava anche la virgoletta di chiusura. Il
 * browser sostituiva poi `"` con `_` nell'attributo `download`,
 * producendo nomi tipo `users_export_<ts>.csv_`.
 */
export const exportUsersReport = async (format = 'csv') => {
  let response;
  try {
    response = await apiClient.get(`/api/v1/admin/users/export?format=${format}`, {
      responseType: 'blob',
    });
  } catch (err) {
    // When ``responseType: 'blob'`` is set, axios returns the error body
    // as a Blob — ``err.response.data`` is NOT parsed JSON. Decode the
    // Blob so the caller can surface the real backend error (auth,
    // server error, etc.) instead of a generic "failed".
    const blob = err.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        let detail = text;
        try {
          const parsed = JSON.parse(text);
          // FastAPI returns 422 validation errors as
          // ``{ detail: [{ loc: [...], msg: "...", type: "..." }, ...] }``.
          // Flatten the array into a readable string instead of letting
          // template-string coercion turn it into "[object Object]".
          if (Array.isArray(parsed.detail)) {
            detail = parsed.detail
              .map((e) => {
                const loc = Array.isArray(e?.loc) ? e.loc.join('.') : '';
                const msg = e?.msg || e?.message || JSON.stringify(e);
                return loc ? `${loc}: ${msg}` : msg;
              })
              .join(' | ');
          } else if (typeof parsed.detail === 'string') {
            detail = parsed.detail;
          } else if (parsed.detail) {
            detail = JSON.stringify(parsed.detail);
          } else if (parsed.message) {
            detail = parsed.message;
          }
        } catch (_) { /* not JSON, keep raw text */ }
        const status = err.response?.status;
        const wrapped = new Error(
          `Export ${format.toUpperCase()} failed${status ? ` (HTTP ${status})` : ''}: ${detail}`
        );
        wrapped.status = status;
        wrapped.detail = detail;
        throw wrapped;
      } catch (decodeErr) {
        // Decoding failed — re-throw the original axios error.
        if (decodeErr instanceof Error && decodeErr.status !== undefined) throw decodeErr;
      }
    }
    throw err;
  }

  // Validate the response actually carries the expected media type — if
  // the backend slipped a JSON error past us with a 200, we would
  // otherwise download a useless ".csv" containing "{detail: ...}".
  const contentType = (response.headers['content-type'] || '').toLowerCase();
  const expectedType = format === 'csv' ? 'text/csv' : 'application/json';
  if (!contentType.includes(expectedType.split('/')[1])) {
    let detail = '';
    try { detail = await response.data.text(); } catch (_) {}
    throw new Error(
      `Export ${format.toUpperCase()} failed: unexpected content-type ${contentType || 'missing'}${detail ? ` — ${detail.slice(0, 200)}` : ''}`
    );
  }

  // Create a blob URL and trigger download
  const blob = new Blob([response.data], {
    type: format === 'csv' ? 'text/csv' : 'application/json'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = `users_export.${format}`;
  if (contentDisposition) {
    // RFC 6266: prefer quoted form ("..."), fall back to token form (no quotes).
    // Avoid greedy `(.+)` which captures the closing `"` — browsers then
    // replace it with `_` in the download filename.
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?"([^"]+)"|filename\*?=(?:UTF-8'')?([^;]+)/i);
    if (filenameMatch) {
      filename = (filenameMatch[1] || filenameMatch[2] || '').trim();
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

export const previewDocument = async (collectionName, docId) => {
  return apiClient.get(`/api/v1/admin/qdrant/collections/${collectionName}/docs/${docId}/preview`);
};

// Qdrant Management (Super Admin Only)
export const listCollections = async () => {
  return apiClient.get('/api/v1/admin/qdrant/collections');
};

export const listDocuments = async (collectionName) => {
  return apiClient.get(`/api/v1/admin/qdrant/collections/${collectionName}/docs`);
};

export const uploadDocument = async (collectionName, file, title, description, tags, chunkSize = 500, onUploadProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (title) formData.append('title', title);
  if (description) formData.append('description', description);
  if (tags) formData.append('tags', tags);
  if (chunkSize) formData.append('chunk_size', chunkSize.toString());
  
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  
  if (onUploadProgress) {
    config.onUploadProgress = (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onUploadProgress(percentCompleted);
    };
  }
  
  return apiClient.post(`/api/v1/admin/qdrant/collections/${collectionName}/upload`, formData, config);
};

export const deleteDocument = async (collectionName, docId) => {
  return apiClient.delete(`/api/v1/admin/qdrant/collections/${collectionName}/docs/${docId}`);
};

export const deleteAllDocuments = async (collectionName) => {
  return apiClient.delete(`/api/v1/admin/qdrant/collections/${collectionName}/docs`);
};

export const getCollectionDetails = async (collectionName) => {
  return apiClient.get(`/api/v1/admin/qdrant/collections/${collectionName}`);
};

export const searchCollection = async (collectionName, query, limit = 10, signal = null) => {
  const config = {};
  if (signal) {
    config.signal = signal;
  }
  return apiClient.post(`/api/v1/admin/qdrant/collections/${collectionName}/search`, {
    query,
    limit
  }, config);
};

// Admin User Management (Super Admin Only)
export const listAdminUsers = async () => {
  return apiClient.get('/api/v1/admin/admins');
};

export const createAdminUser = async (adminData) => {
  return apiClient.post('/api/v1/admin/admins', adminData);
};

export const updateAdminUser = async (adminId, adminData) => {
  return apiClient.put(`/api/v1/admin/admins/${adminId}`, adminData);
};

export const deleteAdminUser = async (adminId) => {
  return apiClient.delete(`/api/v1/admin/admins/${adminId}`);
};

// Super Admin Settings
export const getAgentConfig = async () => {
  return apiClient.get('/api/v1/admin/settings/agent-config');
};

export const getSystemConfig = async () => {
  return apiClient.get('/api/v1/admin/settings/system');
};

export const getApiKeysStatus = async () => {
  return apiClient.get('/api/v1/admin/settings/api-keys');
};


// ── Social Accounts (Admin) ──

export const getSocialAccountsStats = async () => {
  return apiClient.get('/api/v1/admin/social-accounts/stats');
};

export const getSocialAccounts = async (page = 1, pageSize = 20, search = null, isConnected = null, userId = null) => {
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (isConnected !== null) params.is_connected = isConnected;
  if (userId) params.user_id = userId;
  return apiClient.get('/api/v1/admin/social-accounts', { params });
};

export const getSocialAccountDetail = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/${accountId}`);
};

// ── TikTok Social Accounts (Admin) ──

export const getTikTokAccountsStats = async () => {
  return apiClient.get('/api/v1/admin/social-accounts/tiktok/stats');
};

export const getTikTokAccounts = async (page = 1, pageSize = 20, search = null, isConnected = null, userId = null) => {
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (isConnected !== null) params.is_connected = isConnected;
  if (userId) params.user_id = userId;
  return apiClient.get('/api/v1/admin/social-accounts/tiktok', { params });
};

export const getTikTokAccountDetail = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/tiktok/${accountId}`);
};

// ── Facebook Pages (Admin) ──

export const getFacebookAccountsStats = async () => {
  return apiClient.get('/api/v1/admin/social-accounts/facebook/stats');
};

export const getFacebookAccounts = async (page = 1, pageSize = 20, search = null, isConnected = null, userId = null) => {
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (isConnected !== null) params.is_connected = isConnected;
  if (userId) params.user_id = userId;
  return apiClient.get('/api/v1/admin/social-accounts/facebook', { params });
};

export const getFacebookAccountDetail = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/facebook/${accountId}`);
};


// ── Social Account Tokens (SUPERADMIN only) ──
// Every call is audit-logged server-side.

export const getInstagramAccountTokens = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/${accountId}/tokens`);
};

export const getTikTokAccountTokens = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/tiktok/${accountId}/tokens`);
};

export const getFacebookAccountTokens = async (accountId) => {
  return apiClient.get(`/api/v1/admin/social-accounts/facebook/${accountId}/tokens`);
};


// ── Scheduled Videos (Admin) ──

export const getScheduledVideosStats = async () => {
  return apiClient.get('/api/v1/admin/scheduled-videos/stats');
};

export const getScheduledVideos = async (
  page = 1,
  pageSize = 20,
  status = null,
  userId = null,
  batchId = null,
  dateFrom = null,
  dateTo = null,
  sortBy = 'created_at',
  sortOrder = 'desc',
  search = null,
  onlyStuck = false,  // only posts whose batch is stuck (should have finalized)
) => {
  const params = { page, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder };
  if (status) params.status = status;
  if (userId) params.user_id = userId;
  if (batchId) params.batch_id = batchId;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (search) params.search = search;  // owner email or social handle
  if (onlyStuck) params.only_stuck = true;
  return apiClient.get('/api/v1/admin/scheduled-videos', { params });
};

export const getScheduledVideoDetail = async (postId) => {
  return apiClient.get(`/api/v1/admin/scheduled-videos/${postId}`);
};

export const deleteScheduledVideo = async (postId) => {
  return apiClient.delete(`/api/v1/admin/scheduled-videos/${postId}`);
};

export const retryScheduledVideoPublish = async (postId) => {
  return apiClient.post('/api/v1/admin/test-publish/trigger', { post_id: postId });
};

//  /\_/\
// ( o.o )  Audio-failed recovery: rebuild audio+video, or regenerate the
//  > ^ <   script text, for a single post stuck in ``audio_failed``.
export const retryAudioScheduledVideo = async (postId) => {
  return apiClient.post(`/api/v1/admin/scheduled-videos/${postId}/retry-audio`);
};

export const regenerateTextScheduledVideo = async (postId) => {
  return apiClient.post(`/api/v1/admin/scheduled-videos/${postId}/regenerate-text`);
};

export const bulkDeleteScheduledVideos = async (postIds) => {
  return apiClient.post('/api/v1/admin/scheduled-videos/bulk-delete', postIds);
};

export const bulkRetryScheduledVideos = async (postIds) => {
  return apiClient.post('/api/v1/admin/scheduled-videos/bulk-retry', postIds);
};


// ── Feature Flags ──

export const updateUserFeatureFlags = async (userId, featureFlags) => {
  return apiClient.put(`/api/v1/admin/users/${userId}`, { feature_flags: featureFlags });
};

export const bulkUpdateFeatureFlags = async (userIds, featureFlags) => {
  return apiClient.post('/api/v1/admin/users/bulk-feature-flags', {
    user_ids: userIds,
    feature_flags: featureFlags,
  });
};


// ── Database Backups ──

export const getBackupSettings = async () => {
  return apiClient.get('/api/v1/admin/backups/settings');
};

export const updateBackupSettings = async (settings) => {
  return apiClient.put('/api/v1/admin/backups/settings', settings);
};

export const listBackups = async (params = {}) => {
  return apiClient.get('/api/v1/admin/backups', { params });
};

export const triggerBackup = async () => {
  return apiClient.post('/api/v1/admin/backups/trigger');
};

export const deleteBackup = async (backupId) => {
  return apiClient.delete(`/api/v1/admin/backups/${backupId}`);
};

export const downloadBackup = async (backupId) => {
  return apiClient.get(`/api/v1/admin/backups/${backupId}/download`, {
    responseType: 'blob',
  });
};

// System Alerts
export const getSystemAlerts = async (params = {}) => {
  return apiClient.get('/api/v1/admin/alerts', { params });
};

export const getAlertsSummary = async () => {
  return apiClient.get('/api/v1/admin/alerts/summary');
};

export const resolveAlert = async (alertId, resolutionNote = null) => {
  const params = {};
  if (resolutionNote) params.resolution_note = resolutionNote;
  return apiClient.put(`/api/v1/admin/alerts/${alertId}/resolve`, null, { params });
};

export const bulkResolveAlerts = async (alertIds, resolutionNote = null) => {
  const params = { alert_ids: alertIds };
  if (resolutionNote) params.resolution_note = resolutionNote;
  return apiClient.put('/api/v1/admin/alerts/bulk-resolve', null, { params });
};

export const deleteAlert = async (alertId) => {
  return apiClient.delete(`/api/v1/admin/alerts/${alertId}`);
};

// Orphan FB comments — superadmin only.
//
// "Orphan" = a comment SocialInteraction that can no longer be acted
// on (auto-reply marked failed, parent post deleted, owning page
// disconnected). The endpoints classify each row server-side and the
// delete operations are guarded by re-classification, so the admin
// UI cannot remove a healthy comment by sending a stale id.
export const listOrphanComments = async ({ page = 1, page_size = 50, reason, user_id } = {}) => {
  const params = { page, page_size };
  if (reason) params.reason = reason;
  if (user_id != null) params.user_id = user_id;
  return apiClient.get('/api/v1/admin/orphan-comments', { params });
};

export const getOrphanCommentsStats = async () => {
  return apiClient.get('/api/v1/admin/orphan-comments/stats');
};

export const deleteOrphanComment = async (commentId) => {
  return apiClient.delete(`/api/v1/admin/orphan-comments/${commentId}`);
};

export const bulkDeleteOrphanComments = async (ids) => {
  return apiClient.post('/api/v1/admin/orphan-comments/bulk-delete', { ids });
};

// ── Conversation Monitoring (admin + superadmin) ──
//
// Cross-tenant view over WhatsApp / Facebook DMs / Facebook comments /
// Instagram DMs / Instagram comments. The "problematic" flag is
// derived per-query on the backend (4 rules: negative streak, high
// escalation, SLA breach, AI paused without follow-up).
export const listMonitoredConversations = async (params = {}) => {
  return apiClient.get('/api/v1/admin/monitoring/conversations', { params });
};

export const getMonitoredConversationDetail = async (conversationId) => {
  return apiClient.get(
    `/api/v1/admin/monitoring/conversations/${conversationId}`
  );
};

export const getMonitoringStats = async (params = {}) => {
  return apiClient.get('/api/v1/admin/monitoring/stats', { params });
};

export const listMonitoredEscalations = async (params = {}) => {
  return apiClient.get('/api/v1/admin/monitoring/escalations', { params });
};

export const triggerSentimentBackfill = async (limit = 500) => {
  return apiClient.post(
    '/api/v1/admin/monitoring/sentiment-backfill',
    null,
    { params: { limit } }
  );
};

// ── Scheduled posts: reschedule (superadmin only on the backend) ──
//
// Pushes a new ``scheduled_publish_at`` for a not-yet-published post
// (allowed statuses: draft / pending_validation / approved / queued).
// Requires the caller's token to belong to a ``superadmin`` user — the
// API returns 403 otherwise.
export const rescheduleScheduledPost = async (postId, scheduledPublishAtIso) => {
  return apiClient.patch(
    `/api/v1/admin/scheduled-posts/${postId}/reschedule`,
    { scheduled_publish_at: scheduledPublishAtIso }
  );
};

